import {FC, useState} from 'react'
import moment from 'moment'
import {Column, TableProps} from './types'
import TableHeader from './TableHeader'
import Actions from './actions'

const Table: FC<TableProps> = ({
  columns,
  data,
  canRead,
  canEdit,
  canDelete,
  onSorting,
  onSearch,
  currentPage = 1,
  pageSize = 1,
  hasBulkActions = false,
  selectedRows,
  setSelectedRows,
}) => {
  const [sortingField, setSortingField] = useState('')
  const [sortingOrder, setSortingOrder] = useState<'asc' | 'desc'>('asc')

  const [search, setSearch] = useState<any>({})

  const onSortingChange = (field: string) => {
    const order = field === sortingField && sortingOrder === 'asc' ? 'desc' : 'asc'

    setSortingField(field)
    setSortingOrder(order)
    onSorting(field, order)
  }

  const onInputChange = (column: Column, value: any) => {
    if (!column.searchBy && !column.searchList) return

    const searchField = column.searchColumn || column.field

    const searchParams = {
      ...search,
    }

    if (column.searchOperator) {
      searchParams[searchField] = {
        value,
        operator: column.searchOperator,
      }
    } else {
      searchParams[searchField] = value
    }

    if (['', undefined, null].includes(value)) {
      delete searchParams[searchField]
    }

    setSearch(searchParams)
    onSearch(searchParams)
  }

  return (
    <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 table-hover'>
      <thead>
        <TableHeader
          columns={columns}
          sortingField={sortingField}
          sortingOrder={sortingOrder}
          onInputChange={onInputChange}
          onSortingChange={onSortingChange}
          hasBulkActions={hasBulkActions}
          allSelected={data.length !== 0 && selectedRows.length === data.length}
          toggleSelectedRows={() => {
            if (selectedRows.length === data.length) {
              setSelectedRows([])
            } else {
              setSelectedRows(data)
            }
          }}
        />
      </thead>
      <tbody className='text-gray-600 fw-bold'>
        {data.length === 0 ? (
          <tr>
            <td colSpan={columns.length + 1 + (hasBulkActions ? 1 : 0)}>No Record Found</td>
          </tr>
        ) : (
          data.map((row: any, index: number) => (
            <tr
              key={index}
              onClick={() => {
                if (hasBulkActions) {
                  setSelectedRows(
                    selectedRows.includes(row)
                      ? selectedRows.filter((r) => r !== row)
                      : [...selectedRows, row]
                  )
                }
              }}
            >
              {hasBulkActions && (
                <td>
                  <div className='form-check form-check-sm form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={selectedRows.some((selected) => selected.id === row.id)}
                      onChange={() => {
                        if (hasBulkActions) {
                          setSelectedRows?.(
                            selectedRows.includes(row)
                              ? selectedRows.filter((r) => r !== row)
                              : [...selectedRows, row]
                          )
                        }
                      }}
                    />
                  </div>
                </td>
              )}
              <td align='center'>{pageSize * currentPage - pageSize + index + 1}</td>
              {columns.map(
                (col: any, ind: number) =>
                  !(typeof col.hide === 'function' ? col.hide() : col.hide) &&
                  (typeof row[col.field] !== 'undefined' ? (
                    <td key={ind}>
                      {col.component
                        ? col.component(row, col)
                        : col.type === 'date'
                        ? row[col.field] && moment(row[col.field]).isValid()
                          ? moment(row[col.field]).format(col.dateFormat ?? 'DD-MM-YYYY')
                          : ''
                        : row[col.field]}
                    </td>
                  ) : (
                    <td key={ind} className='text-center'>
                      {col.field === 'action' && (
                        <>
                          {col.component && col.component(row, col)}
                          <Actions
                            column={col}
                            row={row}
                            canRead={canRead}
                            canEdit={canEdit}
                            canDelete={canDelete}
                          />
                        </>
                      )}
                    </td>
                  ))
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

export default Table
